/* Vyregistret för Systemadmin (/admin).

   NY VY? Lägg den HÄR, inte i SystemAdminModule.jsx:
     1. en rad i NAV_SECTIONS (key, to, label, Icon; `hiddenInProd: true` om vyn
        bara ska finnas i dev och stage)
     2. en rad i VIEW_ROUTES: { path: '<route under /admin>', render: (ctx) => <DinVy /> }
   ctx bär det vyer får från skalet: user, miljo ({ key, kort, namn, isDev,
   isStage, isProd }).
   Skalet (SystemAdminModule.jsx) är fryst under uppdelningen — se
   docs/visions/systemadmin/systemadmin-genomgang-och-plan-2026-09-28.md §7.

   Menystruktur enligt beslut D4 (2026-09-30): Översikt, Företag & konton,
   KanAI, Drift, Utveckling. "Alla verktyg" och Tidsredovisning är borta
   (D3, D4) — gamla adresser omdirigeras i REDIRECTS. "tools"/"systemverktyg" togs bort i session 10 (D8). */
import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Activity, ArrowRightLeft, BookOpen, Building2, FileSignature, FlaskConical, Inbox,
  KeyRound, Languages, LayoutDashboard, Shield, TrendingUp, Users,
} from 'lucide-react';

const CreateUserPage = lazy(() => import('./users/CreateUserPage'));
const UsersPage = lazy(() => import('./users/UsersPage'));
const TranslatePage = lazy(() => import('./translate/TranslatePage'));
const TrainingKnowledgeLibrary = lazy(() => import('../../components/TrainingKnowledgeLibrary'));
const TenantsPage = lazy(() => import('./tenants/TenantsPage'));
const MigrationPage = lazy(() => import('./migration/MigrationPage'));
const HealthPage = lazy(() => import('./health/HealthPage'));
const SecurityPage = lazy(() => import('./security/SecurityPage'));
const ScriveTestPage = lazy(() => import('./scrive/ScriveTestPage'));
const McpTokensPage = lazy(() => import('./mcpTokens/McpTokensPage'));
const SupportTicketsPage = lazy(() => import('./support/SupportTicketsPage'));
const KanaiUsagePage = lazy(() => import('./kanaiUsage/KanaiUsagePage'));
const DevToolsPage = lazy(() => import('./dev/DevToolsPage'));

/* ─── sidomenyn ─── */
export const NAV_SECTIONS = [
  {
    group: 'Översikt',
    items: [
      { key: 'overview', to: '/admin', end: true, label: 'Startsida', Icon: LayoutDashboard },
    ],
  },
  {
    group: 'Företag & konton',
    items: [
      { key: 'tenants', to: '/admin/tenants', label: 'Företag', Icon: Building2 },
      { key: 'users', to: '/admin/users', label: 'Systemkonton', Icon: Users },
      { key: 'support', to: '/admin/support', label: 'Supportärenden', Icon: Inbox },
    ],
  },
  {
    group: 'KanAI',
    items: [
      { key: 'knowledge', to: '/admin/knowledge', label: 'Kunskapsbibliotek', Icon: BookOpen },
      { key: 'translate', to: '/admin/translate', label: 'Översättning', Icon: Languages },
      { key: 'kanai-usage', to: '/admin/kanai-usage', label: 'KanAI-användning', Icon: TrendingUp },
      { key: 'mcp-tokens', to: '/admin/mcp-tokens', label: 'MCP-tokens', Icon: KeyRound },
    ],
  },
  {
    group: 'Drift',
    items: [
      { key: 'system-health', to: '/admin/system-health', label: 'Systemhälsa', Icon: Activity },
      { key: 'security', to: '/admin/security', label: 'Säkerhet', Icon: Shield },
      { key: 'migration', to: '/admin/migration', label: 'Migrering', Icon: ArrowRightLeft },
    ],
  },
  {
    group: 'Utveckling',
    items: [
      { key: 'scrive-test', to: '/admin/scrive-test', label: 'Scrive-test', Icon: FileSignature },
      { key: 'dev', to: '/admin/dev', label: 'Testverktyg', Icon: FlaskConical, hiddenInProd: true },
    ],
  },
];

/* ─── vyer som skalet monterar under /admin/<path> ─── */
export const VIEW_ROUTES = [
  { path: 'tenants/*', render: () => <TenantsPage /> },
  { path: 'users', render: () => <UsersPage /> },
  { path: 'users/new', render: () => <CreateUserPage /> },
  { path: 'support', render: () => <SupportTicketsPage /> },
  { path: 'knowledge', render: () => <TrainingKnowledgeLibrary mode="system-admin" /> },
  { path: 'translate/*', render: () => <TranslatePage /> },
  { path: 'kanai-usage', render: () => <KanaiUsagePage /> },
  { path: 'mcp-tokens', render: () => <McpTokensPage /> },
  { path: 'system-health', render: () => <HealthPage /> },
  { path: 'security', render: () => <SecurityPage /> },
  { path: 'migration', render: () => <MigrationPage /> },
  { path: 'scrive-test', render: () => <ScriveTestPage /> },
  /* Testverktyg bara i dev och stage (D5). */
  { path: 'dev', render: ({ miljo }) => (miljo.isProd ? <Navigate to="/admin" replace /> : <DevToolsPage isDev={miljo.isDev} />) },
];

/* ─── gamla adresser ─── */
export const REDIRECTS = [
  ['time-reporting', '/tenant-admin'],    // Tidsredovisning finns i Organisationsinställningar (D3)
  ['document-templates', '/tenant-admin'],
];

/* Brödsmulans etiketter för /admin/<segment> (Layout.js läser dem, D8/session 10). */
export const ADMIN_CRUMBS = Object.fromEntries([
  ...NAV_SECTIONS.flatMap((s) => s.items.map((i) => [i.to.replace('/admin/', '').replace('/admin', 'admin'), i.label])),
  ['content-documents', 'Dokument'], ['new', 'Nytt systemkonto'], ['tenants', 'Företag'],
]);
